import { Controller } from "@hotwired/stimulus"

// Cuenta regresiva de una oferta en "Ofertas ⚡": relámpago (fin de la ventana en curso) y
// del día (cierre de la tienda en su última fecha). Con 24 h o más muestra días ("2 días");
// bajo 24 h, HH:MM:SS. El copy del del día ("Solo hoy", "Hasta el dom") lo renderiza el
// servidor al lado, por día calendario.
// Uso:
//   <div data-controller="timer-discount"
//        data-timer-discount-ends-at-value="2024-01-15T18:00:00-03:00"
//        data-action="turbo:morph-element->timer-discount#repaint">
//     <span data-timer-discount-target="display"></span>
//   </div>
export default class extends Controller {
  static targets = ["display"]
  static values = {
    endsAt: String,     // ISO8601 timestamp de cuando termina
    expiredText: { type: String, default: "Fin" }
  }

  // Stimulus lo llama al conectar y cada vez que cambia el valor: un morph (resultados del buscador) puede
  // dejar en este tile otro producto con otro fin, y la cuenta parte de nuevo con ese.
  // El servidor ya incluye el último minuto cuando corresponde al tipo de descuento.
  endsAtValueChanged() {
    this.stop()
    if (!this.endsAtValue) return

    this.endTime = new Date(this.endsAtValue)
    this.element.classList.remove("timer-discount--expired", "timer-discount--urgent")
    this.interval = setInterval(() => this.updateTimer(), 1000)
    this.updateTimer()
  }

  disconnect() {
    this.stop()
  }

  // Un morph deja el span como lo manda el servidor (vacío) y las clases sin urgente/expirado: se repinta
  // en el mismo instante, antes de que el navegador dibuje, en vez de esperar al próximo tick
  repaint() {
    if (this.endTime) this.updateTimer()
  }

  updateTimer() {
    const now = new Date()
    const diff = this.endTime - now

    if (diff <= 0) {
      this.displayTarget.textContent = this.expiredTextValue
      this.element.classList.add("timer-discount--expired")
      this.stop()

      // Opcional: recargar la página cuando expire para actualizar precios
      // setTimeout(() => window.location.reload(), 2000)
      return
    }

    // 24 h o más: en días, que es lo que el cliente entiende de una oferta de fin de semana.
    const DAY = 24 * 60 * 60 * 1000
    if (diff >= DAY) {
      const days = Math.floor(diff / DAY)
      this.displayTarget.textContent = days === 1 ? "1 día" : `${days} días`
      return
    }

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    // Formato: HH:MM:SS o MM:SS si es menos de 1 hora
    let timeString
    if (hours > 0) {
      timeString = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`
    } else {
      timeString = `${this.pad(minutes)}:${this.pad(seconds)}`
    }

    this.displayTarget.textContent = timeString

    // Agregar clase de urgencia cuando queda poco tiempo (menos de 5 minutos)
    if (diff < 5 * 60 * 1000) {
      this.element.classList.add("timer-discount--urgent")
    }
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  pad(num) {
    return num.toString().padStart(2, "0")
  }
}
